import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {DEFAULT_TABLE_STATE} from 'modules/data/data-constants';
import {IDefaultTableStateFlat, TLookupValue} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {normalizeEntityName} from 'utils/common';

import {
    loadAcceptableExtensions,
    loadBranchesLinkingFlag,
    loadBranchNames,
    loadDefaultSignInfo,
    loadDocumentsForSign,
    loadDocumentsForUploadAttach,
    loadDocumentsSigningMethod,
    loadDocumentTypes,
    loadDocumentTypesOnUpload,
    loadFileRowStateDataInEditableTable,
} from '../api/thunks';
import {FILE_UNIQUE_FIELD_NAME, TOTAL_ENTITY} from './constants';
import {
    IDocumentProperty,
    EDocumentsSigningMethod,
    IDocumentsState,
    IDocumentsUploadMonitoringState,
    IDocumentTypeResponse,
    IFileActivityResetAllPayload,
    IFileActivityResetItemPayload,
    IFileActivitySetPayload,
    IFileListDtoPayload,
    IFileListResetPayload,
    IFileListSetPayload,
    ISessionIdSetPayload,
    ISetDocumentsForUploadAttachPayload,
    ISignActivityResetPayload,
    ISignActivitySetPayload,
    ISignListResetPayload,
    ISignListSetPayload,
    ISingSetExtensionsPayload,
} from './types';

const initialState: IDocumentsState = {
    documentTypes: undefined,
    documentTypesOnUpload: undefined,
    branchNames: undefined,
    documentList: undefined,
    fileUpload: {},
    signatureUpload: {},
    signatureExtensions: undefined,
    totalStage: undefined,
    documentsUploadMonitoring: undefined,
    pagination: DEFAULT_TABLE_STATE,
    signingMethod: undefined,
    documentsForUploadAttach: undefined,
    documentProperties: undefined,
    hasBranchesLinkingFlag: false,
};

/**
* Слайс - результат рефакторинга модуля modules/documents
* c ванильного Redux на Redux Toolkit и Thunks.
* Оригинальный модуль устарел и был перенесен в папку .deprecated
* */
const documentsUploadingSlice = createSlice({
    name: 'documents',
    initialState,
    reducers: {
        // == setDocumentsForUploadAttach
        setDocumentsForUploadAttach: (
            state,
            {payload}: PayloadAction<ISetDocumentsForUploadAttachPayload>,
        ) => {
            state.documentsForUploadAttach = {
                ...state.documentsForUploadAttach,
                [payload.sublistCode]: payload.documents,
            };
        },
        // == setDocumentSigningMethod
        setDocumentSigningMethod: (
            state,
            {payload}: PayloadAction<EDocumentsSigningMethod>,
        ) => {
            state.signingMethod = payload;
        },
        // == setDocumentTypes´
        setDocumentTypes: (
            state,
            {payload}: PayloadAction<IDocumentTypeResponse[]>,
        ) => {
            state.documentTypes = payload;
        },
        // == setDocumentTypesOnUpload
        setDocumentTypesOnUpload: (
            state,
            {payload}: PayloadAction<IDocumentTypeResponse[]>,
        ) => {
            state.documentTypesOnUpload = payload;
        },
        // == setDocumentsUploadMonitoring
        setDocumentsUploadMonitoring: (
            state,
            {payload}: PayloadAction<IDocumentsUploadMonitoringState>,
        ) => {
            state.documentsUploadMonitoring = payload;
        },
        // == setDocumentsPagination
        setDocumentsPagination: (
            state,
            {payload}: PayloadAction<IDefaultTableStateFlat>,
        ) => {
            state.pagination = payload;
        },
        // == setBranchNames
        setBranchNames: (
            state,
            {payload}: PayloadAction<IDocumentTypeResponse[]>,
        ) => {
            state.branchNames = payload;
        },
        // == setDocumentProperty
        setDocumentProperty: (
            state,
            {payload}: PayloadAction<IDocumentProperty>,
        ) => {
            state.documentProperties = {
                ...state.documentProperties,
                ...payload,
            };
        },
        // == setDocumentList
        setDocumentList: (
            state,
            {payload}: PayloadAction<TLookupValue[]>,
        ) => {
            state.documentList = payload;
        },
        // == setActivityState
        setFileActivityState: (
            state,
            {payload}: PayloadAction<IFileActivitySetPayload>,
        ) => {
            const {entityName, uniqueValue, item} = payload;
            state.fileUpload[entityName] = state.fileUpload[entityName] || {};
            state.fileUpload[entityName].activityList = state.fileUpload[entityName].activityList || {};
            state.fileUpload[entityName].activityList[uniqueValue] = {
                ...state.fileUpload[entityName].activityList[uniqueValue],
                ...item,
            };
        },
        // == setSessionId
        setSessionId: (
            state,
            {payload}: PayloadAction<ISessionIdSetPayload>,
        ) => {
            const {entityName, sessionId} = payload;
            state.fileUpload[entityName] = state.fileUpload[entityName] || {};
            state.fileUpload[entityName].sessionId = sessionId;
        },
        // == resetItemActivityState
        resetFileActivityState: (
            state,
            {payload}: PayloadAction<IFileActivityResetItemPayload>,
        ) => {
            const {entityName, uniqueValue} = payload;
            if (state.fileUpload[entityName]?.activityList) {
                delete state.fileUpload[entityName].activityList[uniqueValue];
            }
        },
        // == resetActivityState
        resetAllActivityState: (
            state,
            {payload}: PayloadAction<IFileActivityResetAllPayload>,
        ) => {
            if (state.fileUpload[payload.entityName]) {
                state.fileUpload[payload.entityName].activityList = {};
            }
        },
        // == setFileList
        setFileList: (
            state,
            {payload}: PayloadAction<IFileListSetPayload>,
        ) => {
            state.fileUpload[payload.entityName] = {
                ...state.fileUpload[payload.entityName],
                fileList: payload.fileList,
            };
        },
        // == resetDocumentList == resetFileList
        resetFileList: (
            state,
            {payload}: PayloadAction<IFileListResetPayload>,
        ) => {
            if (state.fileUpload[payload.entityName]) {
                state.fileUpload[payload.entityName].fileList = [];
            }
        },
        // == dtoFileList
        dtoFileList: (
            state,
            {payload}: PayloadAction<IFileListDtoPayload>,
        ) => {
            state.totalStage = {fileList: payload.fileList};
        },
        // == setSignActivityState
        setSignActivityState: (
            state,
            {payload}: PayloadAction<ISignActivitySetPayload>,
        ) => {
            const {entityName, uniqueValue, item} = payload;
            state.signatureUpload[entityName] = state.signatureUpload[entityName] || {};
            state.signatureUpload[entityName].activityList = state.signatureUpload[entityName].activityList || {};
            state.signatureUpload[entityName].activityList[uniqueValue] = {
                ...state.signatureUpload[entityName].activityList[uniqueValue],
                ...item,
            };
        },
        // == resetSignActivityState
        resetSignActivityState: (
            state,
            {payload}: PayloadAction<ISignActivityResetPayload>,
        ) => {
            if (state.signatureUpload[payload.entityName]) {
                state.signatureUpload[payload.entityName].activityList = {};
            }
        },
        // == setSignList
        setSignList: (
            state,
            {payload}: PayloadAction<ISignListSetPayload>,
        ) => {
            state.signatureUpload[payload.entityName] = {
                ...state.signatureUpload[payload.entityName],
                signList: payload.signList,
            };
        },
        // == resetSignList
        resetSignList: (
            state,
            {payload}: PayloadAction<ISignListResetPayload>,
        ) => {
            if (state.signatureUpload[payload.entityName]) {
                state.signatureUpload[payload.entityName].signList = [];
            }
        },
        // == setExtensions
        setSignExtensions: (
            state,
            {payload}: PayloadAction<ISingSetExtensionsPayload>,
        ) => {
            state.signatureExtensions = {accept: payload.accept};
        },
        // == clearDocumentsForSign
        clearDocumentsForSign(state) {
            state.documentList = [];
        },
        // == resetFilesUploadProgress
        resetFilesUploadProgress: (state, action: PayloadAction<{ entityName: string; entityType: EEntityType }>) => {
            const {entityName, entityType} = action.payload;
            const normalizedEntityName = normalizeEntityName(entityName);

            if (entityType === EEntityType.TABLE) {
                if (state.fileUpload[normalizedEntityName]) {
                    state.fileUpload[normalizedEntityName].fileList = [];
                }
            }
        },
        /**
         * Очищает список загруженных файлов для указанной сущности.
         */
        clearAllFilesInfo: (state, {payload}: PayloadAction<{ entityName: string }>) => {
            const {entityName} = payload;
            if (state.fileUpload[entityName]) {
                state.fileUpload[entityName].fileList = [];
                state.fileUpload[entityName].activityList = {};
            }
        },
        /**
         * Очищает список загруженных подписей для указанной сущности.
         */
        clearAllSignsInfo: (state, {payload}: PayloadAction<{ entityName: string }>) => {
            const {entityName} = payload;
            if (state.signatureUpload[entityName]) {
                state.signatureUpload[entityName].signList = [];
                state.signatureUpload[entityName].activityList = {};
            }
        },
        resetRowStateDataForEditableTable: (
            state,
            {payload}: PayloadAction<{ tableRow: Record<string, any>; uniqueFieldName?: string; entityName?: string }>,
        ) => {
            const {tableRow, uniqueFieldName = FILE_UNIQUE_FIELD_NAME, entityName = TOTAL_ENTITY} = payload;
            const uniqueValue = tableRow[uniqueFieldName];

            if (uniqueValue && state.fileUpload[entityName]?.activityList) {
                state.fileUpload[entityName] = {
                    ...state.fileUpload[entityName],
                    activityList: Object.fromEntries(
                        Object.entries(state.fileUpload[entityName].activityList).filter(
                            ([key]) => key !== uniqueValue,
                        ),
                    ),
                };
            }
        },
    },
    extraReducers: builder => {
        builder
            .addCase(loadDocumentsForUploadAttach.fulfilled, (state, action) => {
                state.documentList = action.payload.documents;
            })
            .addCase(loadBranchesLinkingFlag.fulfilled, (state, action) => {
                state.hasBranchesLinkingFlag = action.payload;
            })
            .addCase(loadDocumentTypesOnUpload.fulfilled, (state, action) => {
                state.documentTypesOnUpload = action.payload;
            })
            .addCase(loadDocumentTypes.fulfilled, (state, action) => {
                state.documentTypes = action.payload;
            })
            .addCase(loadAcceptableExtensions.fulfilled, (state, action) => {
                state.signatureExtensions = action.payload;
            })
            .addCase(loadDocumentsForSign.fulfilled, (state, action) => {
                state.documentList = action.payload;
            })
            .addCase(loadBranchNames.rejected, state => {
                state.branchNames = [];
            })
            .addCase(loadDocumentsSigningMethod.fulfilled, (state, action) => {
                state.signingMethod = action.payload;
            })
            .addCase(loadDocumentsSigningMethod.rejected, state => {
                state.signingMethod = undefined;
            })
            .addCase(loadBranchNames.fulfilled, (state, {payload}) => {
                state.branchNames = payload;
            })
            .addCase(loadDefaultSignInfo.fulfilled, (state, action) => {
                const {entityName, signInfo} = action.payload;
                state.signatureUpload[entityName] = {
                    ...state.signatureUpload[entityName],
                    signList: signInfo,
                };
            })
            .addCase(loadFileRowStateDataInEditableTable.fulfilled, (state, action) => {
                const {entityName, updatedFile, uniqueFieldName} = action.payload;
                const fileState = state.fileUpload[entityName];

                if (!fileState) return;

                const currentList = fileState.fileList || [];
                const index = currentList.findIndex(f => f[uniqueFieldName] === updatedFile[uniqueFieldName]);

                if (index !== -1) {
                    currentList[index] = {...currentList[index], ...updatedFile};
                } else {
                    currentList.push(updatedFile);
                }

                state.fileUpload[entityName].fileList = currentList;
            });
    },
});

export const {reducer: documentsUploadingSliceReducer} = documentsUploadingSlice;

export const {
    setSignList,
    setSignActivityState,
    resetSignActivityState,
    setFileList,
    resetAllActivityState,
    setFileActivityState,
    setDocumentsUploadMonitoring,
    setSessionId,
    resetFileActivityState,
    setBranchNames,
    resetRowStateDataForEditableTable,
    resetFileList,
    setDocumentsPagination,
    clearAllSignsInfo,
    clearAllFilesInfo,
    clearDocumentsForSign,
} = documentsUploadingSlice.actions;
